import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'cropperjs/dist/cropper.css';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  Button,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import Cropper from 'react-cropper';
import IconButton from '../IconButton';

export default function CropperImage({
  image,
  open,
  setOpen,
  onCropEnd,
  aspectRatio
}) {
  const cropperRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);

  const handleClick = async () => {
    setLoading(true);
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    const canvas = cropper.getCroppedCanvas();

    const promiseBlob = new Promise(resolve => {
      canvas.toBlob(blob => {
        resolve(blob);
      });
    });

    const blob = await promiseBlob;

    const croppedImageUrl = URL.createObjectURL(blob);
    const croppedImage = new File([blob], 'image.jpg', {
      type: 'image/jpeg'
    });

    await onCropEnd({
      file: croppedImage,
      url: croppedImageUrl
    });

    setLoading(false);
  };

  const zoom = type => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    cropper.zoom(type === 'in' ? 0.1 : -0.1);
  };

  const rotate = direction => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    cropper.rotate(direction === 'right' ? 90 : -90);
  };

  const flip = type => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;

    if (type === 'h') {
      cropper.scaleX(scaleX === 1 ? -1 : 1);
      setScaleX(scaleX === 1 ? -1 : 1);
    } else {
      cropper.scaleY(scaleY === 1 ? -1 : 1);
      setScaleY(scaleY === 1 ? -1 : 1);
    }
  };

  const resetCropArea = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    cropper.reset();
  };

  return (
    <Modal
      show={open}
      size="xl"
      onHide={() => {
        setOpen(false);
      }}
      centered
    >
      {!image ? (
        <div>
          <Spinner animation="border" variant="primary" />
          <p>
            Puede tardar unos segundos en cargar la imagen, por favor espere...
          </p>
        </div>
      ) : (
        <>
          <Modal.Header
            className="d-flex flex-column flex-md-row gap-3 justify-content-start"
            closeButton
          >
            {/* Primer Grupo de Botones */}
            <div className="d-flex flex-wrap gap-2 mb-2 mb-md-0">
              <OverlayTrigger
                placement={window.innerWidth <= 768 ? 'bottom' : 'top'}
                overlay={<Tooltip>Rotar a la derecha</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => rotate('left')}
                    icon="rotate-left"
                  />
                </span>
              </OverlayTrigger>
              <OverlayTrigger
                placement={window.innerWidth <= 768 ? 'bottom' : 'top'}
                overlay={<Tooltip>Rotar a la derecha</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => rotate('right')}
                    icon="rotate-right"
                  />
                </span>
              </OverlayTrigger>
            </div>

            {/* Segundo Grupo de Botones */}
            <div className="d-flex flex-wrap gap-2 mb-2 mb-md-0">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Voltear horizontalmente</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => flip('h')}
                    icon="left-right"
                  />
                </span>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Voltear verticalmente</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => flip('v')}
                    icon="up-down"
                  />
                </span>
              </OverlayTrigger>
            </div>

            {/* Tercer Grupo de Botones */}
            <div className="d-flex flex-wrap gap-2 mb-2 mb-md-0">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Alejar</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => zoom('out')}
                    icon="magnifying-glass-minus"
                  />
                </span>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Acercar</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={() => zoom('in')}
                    icon="magnifying-glass-plus"
                  />
                </span>
              </OverlayTrigger>
            </div>

            {/* Botón Restablecer */}
            <div className="d-flex flex-wrap gap-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Restablecer</Tooltip>}
              >
                <span>
                  <IconButton
                    style={{ width: 35, aspectRatio: '1/1' }}
                    className="fs-1 p-0 d-flex justify-content-center align-items-center"
                    onClick={resetCropArea}
                    icon="eraser"
                  />
                </span>
              </OverlayTrigger>
            </div>
          </Modal.Header>

          <Modal.Body>
            <Cropper
              src={image}
              ready={() => {
                setLoading(false);
              }}
              style={{ height: '70vh', width: '100%' }}
              ref={cropperRef}
              autoCropArea={1}
              aspectRatio={aspectRatio}
            />
          </Modal.Body>
        </>
      )}

      <Modal.Footer>
        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="m-0">
              Puede tardar unos segundos en cargar la imagen, por favor
              espere...
            </p>
          </div>
        ) : (
          <div />
        )}
        <style>
          {`
          .button-container {
            width: 100%;
          }

          .button-container > button {
            width: 100%;
          }

          @media (max-width: 576px) {
            .button-container.d-flex > :nth-child(1) {
              order: 2;
            }
            .button-container.d-flex > :nth-child(2) {
              order: 1;
            }
          }
        `}
        </style>
        <div
          className="d-flex flex-column flex-sm-row justify-content-end button-container"
          style={{ gap: '1rem' }}
        >
          {' '}
          <Button
            onClick={() => {
              setOpen(false);
            }}
            variant="secondary"
            style={{ width: '100% !important' }}
          >
            <FontAwesomeIcon
              icon="fa-xmark"
              style={{ marginRight: 5, paddingTop: 4 }}
            />
            Cancelar
          </Button>
          <Button
            onClick={loading || !image ? () => {} : handleClick}
            color="success"
            variant="success"
            disabled={loading || !image}
          >
            {loading ? (
              <Spinner animation="border" variant="primary" />
            ) : (
              <>
                <FontAwesomeIcon
                  icon="fa-check"
                  style={{ marginRight: 5, paddingTop: 4 }}
                />
                Guardar
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

CropperImage.propTypes = {
  image: PropTypes.string,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCropEnd: PropTypes.func.isRequired,
  aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
